import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ChevronRight, ChevronLeft, ChevronDown, CheckCircle2, StickyNote, Loader2, Search, DollarSign, FolderClock, PackageCheck, Layers, TrendingUp, FileText, Upload, Plus, Eye, Link2, Users, CreditCard } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { STAGES, formatUSD } from "@/lib/constants";
import AdminJurisdictions from "@/pages/AdminJurisdictions";
import AdminTeam from "@/pages/AdminTeam";
import AdminLeads from "@/pages/AdminLeads";
import AdminSettings from "@/pages/AdminSettings";
import AdminReview from "@/pages/AdminReview";
import AdminTestimonials from "@/pages/AdminTestimonials";
import AdminLinks from "@/pages/AdminLinks";
import AdminPayments from "@/pages/AdminPayments";
import AdminTranslations from "@/pages/AdminTranslations";
import UploadLinkManager from "@/pages/UploadLinkManager";
import PaymentLinkManager from "@/pages/PaymentLinkManager";
import DocPreviewModal from "@/pages/DocPreviewModal";
import BulkDocRequest from "@/pages/BulkDocRequest";

const STATUS_STYLE = {
  pending_payment: "border-amber-200 bg-amber-50 text-amber-700",
  in_progress: "border-blue-200 bg-blue-50 text-blue-700",
  completed: "border-green-200 bg-green-50 text-green-700",
};
const STATUS_TEXT = { pending_payment: "Pending payment", in_progress: "In progress", completed: "Completed" };

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [custNote, setCustNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [estComp, setEstComp] = useState("");
  const [fetched, setFetched] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [tab, setTab] = useState("requests");
  const [docOpen, setDocOpen] = useState(null);
  const [linkOpen, setLinkOpen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [payOpen, setPayOpen] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ name: "", note: "" });

  const load = useCallback(async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    const res = await api.get("/admin/requests", { params });
    setRequests(res.data.requests || []);
    setFetched(true);
  }, [status, q]);

  const loadMetrics = useCallback(async () => {
    try { const r = await api.get("/admin/metrics"); setMetrics(r.data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user?.is_admin) { load(); loadMetrics(); } }, [user, load, loadMetrics]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_staff) return <Navigate to="/dashboard" replace />;

  const trend = metrics?.revenue_trend || [];
  const maxVal = Math.max(1, ...trend.map((d) => d.amount));
  const trend30total = trend.reduce((a, d) => a + d.amount, 0);
  const fmtDay = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };

  const patch = async (id, body, msg) => {
    setBusyId(id);
    try {
      const res = await api.patch(`/admin/requests/${id}`, body);
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, ...res.data } : r)));
      loadMetrics();
      toast.success(msg);
    } catch { toast.error("Update failed."); } finally { setBusyId(null); }
  };

  const advance = (r) => patch(r.id, { stage: (r.stage || 0) + 1 }, `Advanced to “${STAGES[Math.min((r.stage || 0) + 1, STAGES.length - 1)]}”`);
  const rewind = (r) => patch(r.id, { stage: (r.stage || 0) - 1 }, "Stage moved back");
  const markPaid = (r) => patch(r.id, { mark_paid: true }, "Marked as paid");
  const saveNote = async (r) => {
    if (!noteText.trim() && !custNote.trim() && nextAction === (r.next_action || "") && estComp === (r.est_completion || "")) return;
    const body = {};
    if (noteText.trim()) body.internal_note = noteText;
    if (custNote.trim()) body.customer_note = custNote;
    if (nextAction !== (r.next_action || "")) body.next_action = nextAction;
    if (estComp !== (r.est_completion || "")) body.est_completion = estComp;
    await patch(r.id, body, "Order updated");
    setNoteText(""); setCustNote(""); setNoteOpen(null);
  };

  const applyDocs = (id, docs) => setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, req_documents: docs } : r)));
  const setDocStatus = async (r, docId, status) => {
    try { const res = await api.patch(`/admin/requests/${r.id}/documents/${docId}`, { status }); applyDocs(r.id, res.data.req_documents); toast.success("Document status updated"); }
    catch { toast.error("Update failed."); }
  };
  const addDoc = async (r) => {
    if (!newDoc.name.trim()) return toast.error("Enter a document name.");
    try { const res = await api.post(`/admin/requests/${r.id}/documents`, newDoc); applyDocs(r.id, res.data.req_documents); setNewDoc({ name: "", note: "" }); toast.success("Document requested"); }
    catch { toast.error("Could not add document."); }
  };

  return (
    <div className="min-h-screen grain" data-testid="admin-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24">
        <div className="flex items-center gap-2 text-orange-700"><ShieldCheck className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.2em]">Staff console</span></div>
        <h1 className="mt-2 font-heading text-4xl tracking-tight text-foreground">Manage requests</h1>
        <p className="mt-2 text-muted-foreground">Advance a request through its stages, confirm payment and leave internal notes.</p>

        <div className="mt-6 flex flex-wrap rounded-sm border border-border bg-card p-1" data-testid="admin-tabs">
          {[["requests", "Requests"], ["leads", "Leads"], ["links", "Documents"], ["payments", "Payments"], ...(user.is_admin ? [["catalog", "Jurisdictions & Banking"], ["review", "Compliance Review"], ["testimonials", "Testimonials"], ["translations", "Translations"], ["team", "Team"], ["settings", "Settings"]] : [])].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-sm text-sm transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`admin-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "catalog" && user.is_admin && <AdminJurisdictions onChanged={loadMetrics} />}
        {tab === "review" && user.is_admin && <AdminReview />}
        {tab === "testimonials" && user.is_admin && <AdminTestimonials />}
        {tab === "team" && user.is_admin && <AdminTeam />}
        {tab === "settings" && user.is_admin && <AdminSettings />}
        {tab === "leads" && <AdminLeads />}
        {tab === "links" && <AdminLinks />}
        {tab === "payments" && <AdminPayments />}
        {tab === "translations" && user.is_admin && <AdminTranslations />}

        {tab === "requests" && (<>
        {metrics && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-metrics">
            {[
              { key: "revenue", icon: DollarSign, label: "Collected revenue", value: formatUSD(metrics.paid_revenue), sub: `${formatUSD(metrics.pending_revenue)} pending`, accent: "text-green-700 bg-green-50 border-green-200" },
              { key: "open", icon: FolderClock, label: "Open requests", value: metrics.open_count, sub: `${metrics.total_requests} total`, accent: "text-blue-700 bg-blue-50 border-blue-200" },
              { key: "delivered", icon: PackageCheck, label: "Delivered", value: metrics.delivered_count, sub: "completed", accent: "text-primary bg-secondary border-border" },
              { key: "attach", icon: Layers, label: "Bundle attach-rate", value: `${metrics.bundle_attach_rate}%`, sub: `${metrics.bundle_count} bundles`, accent: "text-orange-700 bg-orange-50 border-orange-200" },
            ].map((m) => (
              <div key={m.key} className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`admin-metric-${m.key}`}>
                <span className={`grid place-items-center h-9 w-9 rounded-sm border ${m.accent}`}><m.icon className="h-4 w-4" /></span>
                <p className="mt-3 font-heading text-2xl tracking-tight text-foreground">{m.value}</p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5">{m.label}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{m.sub}</p>
              </div>
            ))}
          </div>
        )}

        {trend.length > 0 && (
          <div className="mt-4 rounded-xl border border-border bg-card p-5 card-soft" data-testid="admin-revenue-trend">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-green-700" /><p className="text-xs uppercase tracking-wider text-muted-foreground">Revenue · last 30 days</p></div>
              <p className="font-heading text-lg text-foreground">{formatUSD(trend30total)}</p>
            </div>
            <div className="mt-4 flex items-end gap-[3px] h-24">
              {trend.map((d) => {
                const h = d.amount > 0 ? Math.max((d.amount / maxVal) * 100, 6) : 2;
                return <div key={d.date} className={`flex-1 rounded-sm transition-colors ${d.amount > 0 ? "bg-orange-300 hover:bg-orange-500" : "bg-secondary"}`} style={{ height: `${h}%` }} title={`${d.date}: ${formatUSD(d.amount)}`} data-testid={`trend-bar-${d.date}`} />;
              })}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-muted-foreground"><span>{fmtDay(trend[0].date)}</span><span>Today</span></div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[190px] bg-card border-border" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending_payment">Pending payment</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search client, company, package…" className="pl-9 bg-card border-border" data-testid="admin-search" />
          </div>
          <Button variant="outline" onClick={load} className="rounded-sm border-slate-300 bg-card hover:bg-secondary" data-testid="admin-search-btn">Search</Button>
          <Button variant="outline" onClick={() => setBulkOpen((b) => !b)} className="rounded-sm border-orange-300 text-orange-700 bg-card hover:bg-orange-50" data-testid="admin-bulk-toggle"><Users className="h-4 w-4 mr-1.5" /> Bulk request</Button>
        </div>

        {bulkOpen && <div className="mt-6"><BulkDocRequest /></div>}

        <div className="mt-6 space-y-4">
          {fetched && requests.length === 0 && <p className="text-muted-foreground py-10 text-center">No requests found.</p>}
          {requests.map((r, i) => {
            const stage = r.stage || 0;
            const paid = r.payment_status === "paid";
            const saving = (r.line_items || []).filter((li) => li.amount < 0).reduce((a, li) => a + Math.abs(li.amount), 0);
            return (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}
                className="rounded-xl border border-border bg-card p-5 card-soft" data-testid={`admin-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-lg text-foreground">{r.package_name}</h3>
                      {r.category === "Bundle" && <Badge className="rounded-sm border border-orange-200 bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px] uppercase tracking-wider">Bundle</Badge>}
                      <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${STATUS_STYLE[r.status] || STATUS_STYLE.pending_payment}`}>{STATUS_TEXT[r.status] || r.status}</Badge>
                      <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${paid ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{paid ? "Paid" : "Unpaid"}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground truncate">{r.client_name || "—"} · {r.client_email || "—"} · {r.company_name || "—"}</p>
                    <p className="mt-1 text-xs text-muted-foreground font-mono">{r.id} · {formatUSD(r.amount)}{saving > 0 ? ` · saved ${formatUSD(saving)}` : ""}</p>
                  </div>
                  <span className="font-mono text-lg text-primary shrink-0">{formatUSD(r.amount)}</span>
                </div>

                {/* Stage tracker */}
                <div className="mt-4 flex items-center gap-1.5 flex-wrap">
                  {STAGES.map((st, si) => (
                    <span key={st} className={`text-[11px] px-2 py-1 rounded-sm border ${si <= stage ? "border-orange-300 bg-orange-50 text-orange-800" : "border-border bg-secondary/40 text-muted-foreground"}`} data-testid={`admin-stage-${r.id}-${si}`}>
                      {si + 1}. {st}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" disabled={busyId === r.id || stage <= 0} onClick={() => rewind(r)} className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-8 text-xs" data-testid={`admin-rewind-${r.id}`}><ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back</Button>
                  <Button size="sm" disabled={busyId === r.id || stage >= STAGES.length - 1} onClick={() => advance(r)} className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-8 text-xs" data-testid={`admin-advance-${r.id}`}>
                    {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Advance <ChevronRight className="h-3.5 w-3.5 ml-1" /></>}
                  </Button>
                  {!paid && <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => markPaid(r)} className="rounded-sm border-green-300 text-green-700 bg-card hover:bg-green-50 h-8 text-xs" data-testid={`admin-markpaid-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark paid</Button>}
                  <Button size="sm" variant="ghost" onClick={() => { const open = noteOpen === r.id ? null : r.id; setNoteOpen(open); setNoteText(""); setCustNote(""); setNextAction(open ? (r.next_action || "") : ""); setEstComp(open ? (r.est_completion || "") : ""); }} className="text-muted-foreground h-8 text-xs" data-testid={`admin-note-toggle-${r.id}`}><StickyNote className="h-3.5 w-3.5 mr-1" /> Update</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDocOpen(docOpen === r.id ? null : r.id)} className="text-muted-foreground h-8 text-xs" data-testid={`admin-docs-toggle-${r.id}`}><FileText className="h-3.5 w-3.5 mr-1" /> Documents{(r.req_documents || []).length ? ` (${(r.req_documents || []).length})` : ""}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setLinkOpen(linkOpen === r.id ? null : r.id)} className="text-muted-foreground h-8 text-xs" data-testid={`admin-uploadlink-toggle-${r.id}`}><Link2 className="h-3.5 w-3.5 mr-1" /> Upload link</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPayOpen(payOpen === r.id ? null : r.id)} className="text-muted-foreground h-8 text-xs" data-testid={`admin-paymentlink-toggle-${r.id}`}><CreditCard className="h-3.5 w-3.5 mr-1" /> Payment link</Button>
                </div>

                {linkOpen === r.id && <UploadLinkManager req={r} />}
                {payOpen === r.id && <PaymentLinkManager req={r} />}

                {docOpen === r.id && (
                  <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-4 space-y-3" data-testid={`admin-docs-${r.id}`}>
                    {(r.req_documents || []).length === 0 && <p className="text-xs text-muted-foreground">No documents yet. Request one from the client below.</p>}
                    {(r.req_documents || []).map((d) => (
                      <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2 last:border-0" data-testid={`admin-doc-${d.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground">{d.name}</p>
                          {d.filename
                            ? <button onClick={() => setPreview(d)} className="text-xs text-muted-foreground hover:text-orange-700 inline-flex items-center gap-1" data-testid={`admin-doc-view-${d.id}`}><Eye className="h-3.5 w-3.5" /> {d.filename}</button>
                            : <span className="text-xs text-muted-foreground">Awaiting client upload</span>}
                        </div>
                        <Select value={d.status} onValueChange={(v) => setDocStatus(r, d.id, v)}>
                          <SelectTrigger className="w-[150px] h-8 bg-card border-border text-xs" data-testid={`admin-doc-status-${d.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["required", "uploaded", "under_review", "approved", "rejected", "action_required"].map((st) => <SelectItem key={st} value={st} className="text-xs">{st.replace(/_/g, " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                    <div className="flex items-end gap-2 pt-1">
                      <Input value={newDoc.name} onChange={(e) => setNewDoc((n) => ({ ...n, name: e.target.value }))} placeholder="Request a document (e.g. Passport)" className="h-8 bg-card border-border text-sm" data-testid={`admin-doc-name-${r.id}`} />
                      <Button size="sm" onClick={() => addDoc(r)} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs shrink-0" data-testid={`admin-doc-add-${r.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Request</Button>
                    </div>
                  </div>
                )}

                {noteOpen === r.id && (
                  <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3 space-y-2" data-testid={`admin-note-box-${r.id}`}>
                    <Textarea value={custNote} onChange={(e) => setCustNote(e.target.value)} placeholder="Progress update visible to the client…" className="min-h-[50px] bg-card border-border text-sm" data-testid={`admin-custnote-input-${r.id}`} />
                    <div className="flex gap-2">
                      <Input value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="Next action (client-visible)" className="h-8 bg-card border-border text-sm" data-testid={`admin-nextaction-${r.id}`} />
                      <Input value={estComp} onChange={(e) => setEstComp(e.target.value)} placeholder="Est. completion (estimate)" className="h-8 bg-card border-border text-sm w-48 shrink-0" data-testid={`admin-estcomp-${r.id}`} />
                    </div>
                    <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Internal note (staff only — never shown to client)…" className="min-h-[50px] bg-card border-border text-sm" data-testid={`admin-note-input-${r.id}`} />
                    <div className="flex justify-end">
                      <Button size="sm" disabled={busyId === r.id} onClick={() => saveNote(r)} className="rounded-sm bg-primary text-primary-foreground h-8 text-xs" data-testid={`admin-note-save-${r.id}`}>Save update</Button>
                    </div>
                  </div>
                )}

                {(r.internal_notes || []).length > 0 && (
                  <div className="mt-3 space-y-1.5 border-t border-border pt-3" data-testid={`admin-notes-${r.id}`}>
                    {(r.internal_notes || []).map((n, ni) => (
                      <p key={ni} className="text-xs text-muted-foreground"><span className="text-foreground">{n.by || "staff"}:</span> {n.text}</p>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
        </>)}
      </div>
      <DocPreviewModal doc={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
